import { motion } from 'framer-motion'
import * as React from 'react'
import { cn } from '#app/utils/misc'

export interface ButtonProps
	extends Omit<
		React.ButtonHTMLAttributes<HTMLButtonElement>,
		| 'onDrag'
		| 'onDragEnd'
		| 'onDragStart'
		| 'onAnimationStart'
		| 'onAnimationEnd'
	> {
	variant?: 'default' | 'outline' | 'ghost'
	size?: 'default' | 'sm' | 'lg'
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
	(
		{ className, variant = 'default', size = 'default', children, ...props },
		ref,
	) => {
		const baseStyles =
			'inline-flex items-center justify-center rounded-lg font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 cursor-pointer'

		const variants = {
			default: 'p-[3px] relative',
			outline: 'p-[3px] relative',
			ghost: 'p-[3px] relative',
		}

		const sizes = {
			default: 'h-10 px-6 py-2 text-sm',
			sm: 'h-8 px-4 text-xs',
			lg: 'h-12 px-8 text-base',
		}

		// All variants use the same nested structure with p-[3px] outer padding
		const innerVariants = {
			default:
				'group relative inline-flex w-full items-center justify-center rounded-[6px] bg-black text-white transition duration-200 hover:bg-transparent',
			outline:
				'group relative inline-flex w-full items-center justify-center rounded-[6px] border border-border bg-transparent text-text transition duration-200 hover:bg-surface-hover',
			ghost:
				'group relative inline-flex w-full items-center justify-center rounded-[6px] bg-transparent text-text transition duration-200 hover:bg-surface-hover',
		}

		return (
			<motion.button
				ref={ref}
				className={cn(baseStyles, variants[variant], className)}
				{...props}
			>
				{variant === 'default' && (
					<div className="bg-primary absolute inset-0 rounded-lg" />
				)}
				<div className={cn(innerVariants[variant], sizes[size])}>
					{children}
				</div>
			</motion.button>
		)
	},
)

Button.displayName = 'Button'

export { Button }
